import React, { useState } from "react";
import { Box, Button, Drawer, Switch, Typography } from "@mui/material";
import BoldLabel from "../../UIComponents/BoldLevel";
import MuiInput from "../../UIComponents/MuiInput";
import MuiSelectAndSearch from "../../UIComponents/MuiSelectAndSearch";
import MuiTextarea from "../../UIComponents/MuiTextarea";
import axios from "axios";
import { getToken } from "../../utils/token";
import { toast } from "react-toastify";

const typeOption = [
  { id: "STRING", name: "String" },
  { id: "NUMBER", name: "Number" },
  { id: "BOOLEAN", name: "Boolean" },
  { id: "ARRAY", name: "Array" },
];

const initialData = {
  name: "",
  type: {},
  description: "",
  active: true,
};

const initialErrors = {
  name: "",
  type: "",
  description: "",
};

export default function CreateRuleAttributeDrawer({
  open,
  handleDrawerClose,
  setFlag,
}) {
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState(initialErrors);

  const handleData = (key, value) => {
    setData({
      ...data,
      [key]: value,
    });
    // Clear error when user starts typing
    setErrors({
      ...errors,
      [key]: "",
    });
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...initialErrors };

    // Check name
    if (!data.name || data.name.trim() === "") {
      newErrors.name = "Name is required";
      isValid = false;
    } else if (/\s/.test(data.name)) {
      newErrors.name = "Name cannot contain spaces";
      isValid = false;
    }

    // Check type
    if (!data.type || !data.type.id) {
      newErrors.type = "Type is required";
      isValid = false;
    }

    // Check description
    if (!data.description || data.description.trim() === "") {
      newErrors.description = "Description is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    const token = getToken();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const body = {
      attributeName: data.name,
      description: data.description,
      status: data.active ? "ACTIVE" : "INACTIVE",
      entity: "LOYALTY",
      attributeValueType: data.type?.id,
      operator:
        data.type?.id === "ARRAY"
          ? ["IN"]
          : data.type?.id === "BOOLEAN"
          ? ["EQUALS", "NOT_EQUALS"]
          : data.type?.id === "STRING"
          ? ["EQUALS", "NOT_EQUALS", "CONTAINS", "NOT_CONTAINS"]
          : [
              "EQUALS",
              "NOT_EQUALS",
              "LESS_THAN",
              "GREATER_THAN",
              "LESS_THAN_OR_EQUAL",
              "GREATER_THAN_OR_EQUAL",
            ],
    };
    axios
      .post(`${baseUrl}/api/rule-attributes`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        console.log(res.data);
        toast.success("Attribute created successfully");
        handleDrawerClose();
        setData(initialData);
        setErrors(initialErrors);
        setFlag((prev) => !prev);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Unable to create attribute");
      });
  };

  // Error message style
  const errorStyle = {
    color: "#d32f2f",
    fontFamily: "Lato",
    fontSize: "12px",
    marginTop: "4px",
  };

  return (
    <Drawer
      open={open}
      onClose={handleDrawerClose}
      anchor="right"
      sx={{
        "& .MuiDrawer-paper": {
          width: "352px",
        },
      }}
    >
      <Box
        sx={{
          marginX: "16px",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "Lato",
              color: "#1D1F2C",
              fontSize: "20px",
              fontWeight: "600",
              lineHeight: "normal",
              marginY: "24px",
            }}
          >
            Add New Attribute
          </Typography>

          {/* Form */}
          <Box>
            <Box>
              <BoldLabel label="Type" mt={"14px"} required={true} />
              <MuiSelectAndSearch
                value={data.type}
                onChange={(e) => handleData("type", e)}
                options={typeOption}
                placeholder={"Search / Select"}
                // error={!!errors.type}
                errorMessage={errors.type}
              />
            </Box>
            <Box>
              <BoldLabel label="Name" mt={"14px"} required={true} />
              <MuiInput
                value={data.name}
                onChange={(e) => handleData("name", e)}
                placeholder={"Eg: minimum transaction"}
                // error={!!errors.name}
                errorMessage={errors.name}
              />
            </Box>
            <Box>
              <BoldLabel label="Description" mt={errors.name ? "34px" : "14px"} required={true} />
              <MuiTextarea
                value={data.description}
                onChange={(e) => handleData("description", e)}
                minRows={6}
                placeholder={"eg: Don't miss our End of Season Sale! Up to 50% off on select items. Refresh your wardrobe with the latest styles at unbeatable prices. Hurry, while stocks last!"}
                // error={!!errors.description}
                errorMessage={errors.description}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "9px 0px",
            gap: "8px",
            backgroundColor: "#F9F9F9",
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            width: "100%",
            zIndex: 10,
          }}
        >
          <Button
            color="inherit"
            sx={{
              color: "#003D86",
              fontFamily: "Lato",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16.8px",
              border: "1px solid #003D86",
              width: "114px",
              height: "30px",
              borderRadius: "6px",
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: "#E8F0F9",
              },
            }}
            onClick={handleDrawerClose}
          >
            Cancel
          </Button>
          <Button
            sx={{
              mr: "20px",
              color: "#FFFFFF",
              fontFamily: "Lato",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16.8px",
              backgroundColor: "#003D86",
              width: "114px",
              height: "30px",
              borderRadius: "6px",
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: "#003D86",
              },
            }}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}